import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  BarChart3, Users, Home, Briefcase, Heart, Target, FileText,
  Download, RefreshCw, TrendingUp, Calendar, Clock, CheckCircle2,
  ArrowUpRight, BookOpen, HandHeart
} from 'lucide-react';

const PERIODS = [
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
  { value: 'quarter', label: 'Last 90 Days' },
  { value: 'year', label: 'Last Year' },
];

function MetricCard({ icon: Icon, label, value, sub, color = 'teal', large, onClick }) {
  return (
    <Card className={`border-0 shadow-sm rounded-xl ${onClick ? 'cursor-pointer hover:ring-2 hover:ring-teal-200 transition-all' : ''}`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`} onClick={onClick}>
      <CardContent className={`${large ? 'p-5' : 'p-4'} text-center`}>
        <Icon size={large ? 28 : 22} className={`mx-auto mb-1.5 text-${color}-600`} />
        <p className={`${large ? 'text-3xl' : 'text-2xl'} font-bold`}>{value}</p>
        <p className={`text-xs ${sub ? '' : 'text-muted-foreground'}`}>{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function ImpactDashboardPage() {
  const { axiosAuth } = useAuth();
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosAuth();
      const [summaryRes, snapshotsRes] = await Promise.all([
        api.get('/impact/summary', { params: { period } }).catch(() => ({ data: { metrics: {}, period_start: '', period_end: '' } })),
        api.get('/impact/snapshots').catch(() => ({ data: [] }))
      ]);
      setData(summaryRes.data);
      setSnapshots(snapshotsRes.data);
    } catch {
      // silently handle - data defaults are set above
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, period]);

  useEffect(() => { loadData(); }, [loadData]);

  const generateSnapshot = async () => {
    try {
      const api = axiosAuth();
      await api.post('/impact/snapshot', { period_type: period === 'week' ? 'weekly' : period === 'month' ? 'monthly' : period === 'quarter' ? 'quarterly' : 'annual' });
      toast.success('Impact snapshot saved');
      loadData();
    } catch {
      toast.error('Failed to generate snapshot');
    }
  };

  const exportCSV = async () => {
    try {
      const api = axiosAuth();
      const res = await api.get('/impact/export', { params: { period, format: 'csv' }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `impact_report_${period}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch {
      toast.error('Failed to export');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const m = data?.metrics || {};

  return (
    <div className="space-y-6" data-testid="impact-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Impact Summary
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data?.period_start} to {data?.period_end}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 rounded-full" data-testid="period-selector">
              <Calendar size={14} className="mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadData} className="rounded-full gap-1">
            <RefreshCw size={14} />
          </Button>
          <Button variant="outline" onClick={exportCSV} className="rounded-full gap-1" data-testid="export-csv-btn">
            <Download size={14} /> CSV
          </Button>
          <Button onClick={generateSnapshot} className="rounded-full gap-1 bg-teal-600 hover:bg-teal-700" data-testid="save-snapshot-btn">
            <FileText size={14} /> Save Snapshot
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <MetricCard icon={Users} label="Participants Served" value={m.participants_served || 0} color="teal" large onClick={() => window.location.href = '/clients'} />
        <MetricCard icon={Users} label="New Participants" value={m.new_participants || 0} sub="this period" color="blue" />
        <MetricCard icon={Home} label="Housing Placements" value={m.housing_placements || 0} color="emerald" onClick={() => window.location.href = '/housing'} />
        <MetricCard icon={Briefcase} label="Employment Services" value={m.employment_services || 0} color="amber" onClick={() => window.location.href = '/service-ledger'} />
        <MetricCard icon={HandHeart} label="Peer Sessions" value={m.peer_sessions_delivered || 0} color="purple" onClick={() => window.location.href = '/group-sessions'} />
      </div>

      {/* Activity & Engagement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 size={16} className="text-teal-600" /> Activity Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2"><BookOpen size={16} className="text-teal-600" /><span className="text-sm">Group Sessions</span></div>
              <div className="text-right">
                <span className="font-bold text-lg">{m.group_sessions_held || 0}</span>
                <p className="text-xs text-muted-foreground">{m.group_attendance_total || 0} total attendance</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2"><Heart size={16} className="text-rose-500" /><span className="text-sm">Check-ins Completed</span></div>
              <span className="font-bold text-lg">{m.checkins_completed || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2"><Target size={16} className="text-amber-500" /><span className="text-sm">Goals Completed</span></div>
              <span className="font-bold text-lg">{m.goals_completed || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2"><Users size={16} className="text-blue-500" /><span className="text-sm">Referrals Received</span></div>
              <span className="font-bold text-lg">{m.referrals_received || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-teal-50">
              <div className="flex items-center gap-2"><Clock size={16} className="text-teal-600" /><span className="text-sm font-medium">Total Service Hours</span></div>
              <span className="font-bold text-xl text-teal-700">{m.service_hours_total || 0}h</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp size={16} className="text-emerald-600" /> Outcome Improvements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-xl bg-emerald-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-emerald-800">Housing Stability</span>
                <Badge className="bg-emerald-100 text-emerald-700 border-0">{m.housing_improvements || 0} improved</Badge>
              </div>
              <Progress value={m.housing_improvements ? Math.min(100, m.housing_improvements * 20) : 0} className="h-2" />
            </div>
            <div className="p-4 rounded-xl bg-blue-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-800">Employment</span>
                <Badge className="bg-blue-100 text-blue-700 border-0">{m.employment_improvements || 0} improved</Badge>
              </div>
              <Progress value={m.employment_improvements ? Math.min(100, m.employment_improvements * 20) : 0} className="h-2" />
            </div>
            <div className="p-4 rounded-xl bg-purple-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-purple-800">Recovery Engagement</span>
                <Badge className="bg-purple-100 text-purple-700 border-0">{m.recovery_improvements || 0} improved</Badge>
              </div>
              <Progress value={m.recovery_improvements ? Math.min(100, m.recovery_improvements * 20) : 0} className="h-2" />
            </div>

            {/* Saved Snapshots */}
            {snapshots.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-muted-foreground mb-2">Recent Snapshots</p>
                <div className="space-y-1.5">
                  {snapshots.slice(0, 3).map(s => (
                    <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-xs">
                      <span className="capitalize">{s.period_type}</span>
                      <span className="text-muted-foreground">{new Date(s.generated_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
